/**
 * Incident Editor Dialog - Relationships Tab (PROJ-007)
 * parentIncidentId, configurationItemId, locationId
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Link2 } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';

interface RelationshipsTabProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function RelationshipsTab({ form, isViewMode = false }: RelationshipsTabProps) {
  return (
    <div className="space-y-6">
      <Alert>
        <Link2 className="h-4 w-4" />
        <AlertDescription>
          Relaciones con otros incidentes, activos y ubicaciones
        </AlertDescription>
      </Alert>

      {/* Parent Incident */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Jerarquía de Incidentes</h3>

        <FormField
          control={form.control}
          name="parentIncidentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Incidente Padre (UUID)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={isViewMode}
                />
              </FormControl>
              <FormDescription>
                Si este incidente es hijo de otro incidente (para duplicados, relacionados, etc.)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Configuration Item (CMDB) */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Activo Afectado (CMDB)</h3>

        <FormField
          control={form.control}
          name="configurationItemId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Configuration Item (UUID)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={isViewMode}
                />
              </FormControl>
              <FormDescription>
                Sistema, servidor, aplicación o activo afectado por el incidente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      {/* Location */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Ubicación</h3>

        <FormField
          control={form.control}
          name="locationId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ubicación (UUID)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  disabled={isViewMode}
                />
              </FormControl>
              <FormDescription>
                Ubicación física o lógica donde ocurrió el incidente
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Alert>
        <AlertDescription>
          <strong>Nota:</strong> Estos campos permiten vincular el incidente con otros registros del sistema para análisis y reportes
        </AlertDescription>
      </Alert>
    </div>
  );
}
